import { MODEL_VERSION } from '@/app/constants';
import type { FormParams } from '@/app/stores/slices/task-form';
import type {
  AllModelVersion,
  FormImagePrompt,
  ModelVersion,
  ReferencePrompt,
  SupportedLanguage,
  TextPromptWithInputItem,
} from '@/app/types';
import { getMaxImageAndElementCount } from './get-max-image-and-element-count';
import { isReferenceAssetSupported } from './is-reference-asset-supported';
import { getNextImageName } from './rename-image';
import {
  appendUnMentionedReferences,
  parseTextToTextPromptWithReference,
  stringifyTextPromptWithReference,
} from './text-prompt-with-reference';

export type ModelVersionAssetSyncUpdates = Partial<
  Pick<FormParams, 'imagePrompts' | 'referencePrompts' | 'textPrompt' | 'textPromptWithReference'>
> & {
  // 需要存储的隐藏项，用于模型版本切换回来时恢复
  // 如果为空数组，会替换掉之前存储的隐藏项（用于清除已删除的项）
  hiddenItems?: ReferencePrompt[];
};

/**
 * 判断模型版本是否支持 video 类型的参考
 */
const isVideoSupport = (version: AllModelVersion): boolean => {
  return version === MODEL_VERSION.Q2_PRO;
};

/**
 * 模型能力 key（用于缓存隐藏项）
 * 由图片/主体上限 + video 参考支持能力组成。
 */
export const getModelCapabilityKey = (version: AllModelVersion): string => {
  const maxCount = getMaxImageAndElementCount(version);
  const supportVideo = isVideoSupport(version) ? 1 : 0;
  return `${maxCount}_${supportVideo}`;
};

/**
 * 根据模型版本切换，生成图片/参考与文本的同步更新
 * - 从非主体资产化 → 主体资产化：imagePrompts → referencePrompts，并构建 textPromptWithReference
 * - 从主体资产化 → 非主体资产化：referencePrompts(图片) → imagePrompts，并将 textPromptWithReference 转为 textPrompt
 * - 主体资产化的情况下处理模型版本切换时，图片/主体数量的变化和 video 支持的变化，同时恢复之前隐藏的图片/主体
 */
export function syncImageReferencePrompts(options: {
  prevVersion: AllModelVersion; // 做同款可能用到，因此包含已经下线的模型版本
  nextVersion: ModelVersion;
  formParams: Partial<FormParams>;
  locale: SupportedLanguage;
  hiddenReferencePrompts?: Partial<Record<string, ReferencePrompt[]>>;
}): ModelVersionAssetSyncUpdates {
  const { prevVersion, nextVersion, formParams, locale, hiddenReferencePrompts = {} } = options;
  const prevSupports = isReferenceAssetSupported(prevVersion, 'character2video');
  const nextSupports = isReferenceAssetSupported(nextVersion, 'character2video');

  // ============================================
  // 第一部分：处理非主体资产化 ↔ 主体资产化的转换
  // ============================================

  // 非主体资产化 → 主体资产化
  if (!prevSupports && nextSupports) {
    const imagePrompts = formParams.imagePrompts || [];
    const referencePrompts = formParams.referencePrompts || [];
    const newReferencePrompts = mergeImagePromptsIntoReferencePrompts(imagePrompts, referencePrompts, locale);

    const textPrompt = formParams.textPrompt || '';
    let newTextPromptWithReference: TextPromptWithInputItem = [];
    if (textPrompt) {
      // 已有 textPrompt，需要 parse 为 textPromptWithReference，再把未提及的参考添加到末尾
      newTextPromptWithReference = appendUnMentionedReferences(
        parseTextToTextPromptWithReference(textPrompt, newReferencePrompts),
        newReferencePrompts,
      );
    } else {
      newTextPromptWithReference = appendUnMentionedReferences([], newReferencePrompts);
    }

    return {
      referencePrompts: newReferencePrompts,
      textPromptWithReference: newTextPromptWithReference,
    };
  }

  // 主体资产化 → 非主体资产化
  if (prevSupports && !nextSupports) {
    const referencePrompts = formParams.referencePrompts || [];
    const imageItems = referencePrompts.filter((item) => item.type === 'image').slice(0, 3);
    const newImagePrompts = imageItems.map((item) => ({
      id: item.id,
      name: item.name,
      status: item.status,
      prompt: item.prompt,
    }));

    const textPromptWithReference = formParams.textPromptWithReference || [];
    // 过滤掉不在 referencePrompts 中的 mention
    const filtered = textPromptWithReference.filter((node) => {
      if (node.type === 'mention') {
        return referencePrompts.some((ref) => ref.id === node.mention.id);
      }
      return true;
    });
    const newTextPrompt = stringifyTextPromptWithReference(filtered);

    return {
      imagePrompts: newImagePrompts,
      textPrompt: newTextPrompt,
    };
  }

  // ============================================
  // 第二部分：处理主体资产化内部的版本切换
  // （maxCount 变化和 video 支持的变化）
  // ============================================

  // 如果都不支持主体资产化，直接返回空
  if (!prevSupports && !nextSupports) {
    return {};
  }

  // 以下逻辑仅在主体资产化支持的范围内执行
  const prevMaxCount = getMaxImageAndElementCount(prevVersion);
  const nextMaxCount = getMaxImageAndElementCount(nextVersion);
  const prevSupportVideo = isVideoSupport(prevVersion);
  const nextSupportVideo = isVideoSupport(nextVersion);
  const nextCapabilityKey = getModelCapabilityKey(nextVersion);

  // 如果都没有变化，直接返回空
  if (prevMaxCount === nextMaxCount && prevSupportVideo === nextSupportVideo) {
    return {};
  }

  const referencePrompts = formParams.referencePrompts || [];
  const imageAndElementPrompts = referencePrompts.filter(
    (item) =>
      (item.type === 'image' || (item.type === 'element' && item.prompt?.modality !== 'video')) &&
      item.status === 'done',
  );
  const videoPrompts = referencePrompts.filter(
    (item) =>
      (item.type === 'video' || (item.type === 'element' && item.prompt?.modality === 'video')) &&
      item.status === 'done',
  );

  let hasRestored = false;

  // ============================================
  // 2.1：处理 maxCount 变化（图片/主体数量的变化）
  // ============================================
  let processedImageAndElementPrompts = imageAndElementPrompts;
  let hiddenImageAndElementItems: ReferencePrompt[] = [];

  // 情况1：从少到多（例如：4 -> 7），需要恢复之前隐藏的
  if (prevMaxCount < nextMaxCount) {
    const restoredItems = (hiddenReferencePrompts[nextCapabilityKey] || []).filter(
      (item) => item.type !== 'video' && !(item.type === 'element' && item.prompt?.modality === 'video'),
    );
    // 去重：获取当前已存在的 id 集合，避免恢复时重复添加已存在的主体
    const existingIds = new Set(imageAndElementPrompts.map((item) => item.id));
    // 只恢复不在当前列表中的项（保留前面的主体，避免重复；且排除视频类型）
    const uniqueRestoredItems = restoredItems.filter((item) => !existingIds.has(item.id));
    processedImageAndElementPrompts = [...imageAndElementPrompts, ...uniqueRestoredItems].slice(0, nextMaxCount);
    hasRestored = uniqueRestoredItems.length > 0;
  }
  // 情况2：从多到少（例如：7 -> 4），需要隐藏多余的
  else if (prevMaxCount > nextMaxCount) {
    processedImageAndElementPrompts = imageAndElementPrompts.slice(0, nextMaxCount);
    hiddenImageAndElementItems = imageAndElementPrompts.slice(nextMaxCount);
  }

  // ============================================
  // 2.2：处理 video 支持的变化
  // ============================================
  let finalReferencePrompts: ReferencePrompt[] = [];
  let hiddenVideoItems: ReferencePrompt[] = [];
  let processedVideoPrompts = videoPrompts;

  // 情况1：从支持 video 切换到不支持 video（Q2Video -> 其他版本）
  if (prevSupportVideo && !nextSupportVideo) {
    // 视频保留但不带过去，隐藏视频
    hiddenVideoItems = videoPrompts;
    finalReferencePrompts = processedImageAndElementPrompts;
  }
  // 情况2：从不支持 video 切换到支持 video（其他版本 -> Q2Video）
  else if (!prevSupportVideo && nextSupportVideo) {
    // 恢复之前隐藏的视频
    const restoredVideoItems = (hiddenReferencePrompts[nextCapabilityKey] || []).filter(
      (item) => item.type === 'video' || (item.type === 'element' && item.prompt?.modality === 'video'),
    );
    // 去重：获取当前已存在的视频 id 集合，避免恢复时重复添加已存在的视频
    const existingVideoIds = new Set(videoPrompts.map((item) => item.id));
    // 只恢复不在当前列表中的视频（保留前面的视频，避免重复）
    const uniqueRestoredVideos = restoredVideoItems.filter((item) => !existingVideoIds.has(item.id));
    processedVideoPrompts = [...videoPrompts, ...uniqueRestoredVideos];
    // 视频在前，图片/主体在后
    finalReferencePrompts = [...processedVideoPrompts, ...processedImageAndElementPrompts];
    hasRestored = uniqueRestoredVideos.length > 0;
  }
  // 情况3：都支持或都不支持 video，直接使用处理后的图片/主体
  else {
    finalReferencePrompts = prevSupportVideo
      ? [...processedVideoPrompts, ...processedImageAndElementPrompts]
      : processedImageAndElementPrompts;
  }

  // ============================================
  // 2.3：更新 textPromptWithReference
  // ============================================
  const textPromptWithReference = formParams.textPromptWithReference || [];
  let newTextPromptWithReference: TextPromptWithInputItem = textPromptWithReference;

  // 过滤掉隐藏项的 mention
  const hiddenIds = new Set([
    ...hiddenImageAndElementItems.map((item) => item.id),
    ...hiddenVideoItems.map((item) => item.id),
  ]);
  if (hiddenIds.size > 0) {
    newTextPromptWithReference = newTextPromptWithReference.map((node) => {
      if (node.type === 'mention') {
        return hiddenIds.has(node.mention.id)
          ? {
              type: 'text',
              text: `[@${node.mention.name}]`,
            }
          : node;
      }
      return node;
    });
  }

  // 添加恢复项的 mention（如果还没有被提及）
  if (hasRestored) {
    newTextPromptWithReference = appendUnMentionedReferences(
      parseTextToTextPromptWithReference(
        stringifyTextPromptWithReference(newTextPromptWithReference),
        finalReferencePrompts,
      ),
      finalReferencePrompts,
    );
  }

  // ============================================
  // 返回结果
  // ============================================
  const allHiddenItems = [...hiddenImageAndElementItems, ...hiddenVideoItems];
  return {
    referencePrompts: finalReferencePrompts,
    textPromptWithReference: newTextPromptWithReference,
    // 如果 allHiddenItems 为空数组，也会返回空数组（而不是 undefined）
    // 这样在 ModelSelection 中调用 setHiddenReferencePrompts 时，会用空数组替换之前存储的隐藏项
    hiddenItems: allHiddenItems.length > 0 ? allHiddenItems : [],
  };
}

/**
 * 将表单中的imagePrompts合并到referencePrompts中
 *
 * 使用场景：
 * - 用户在表单中上传/更换图片后，需要将这些图片同步到引用提示中
 * - 在模型版本切换（如切换到 Q1）时，需要保持图片数据的一致性
 * - 表单回填时，需要将存储的引用提示转换回表单可用的格式
 * 处理过程图示：
 *
 * 输入阶段（imagePrompts最多3个）：
 * imagePrompts:     [图片1] [null] [图片2]
 * referencePrompts: [图片A] [主体1] [图片B] [    ] [    ]
 *
 * 第二步：复制引用提示数组
 * newReferencePrompts: [图片A] [主体1] [图片B] [    ] [    ]
 *
 * 第三步：遍历替换图片类型
 * 处理前: [图片A] [主体1] [图片B] [    ] [    ]
 *          ↓        ↓        ↓      ↓      ↓
 * 处理后: [图片1] [主体1] [null] [图片 2]] [    ]
 *
 * 第四步：过滤null值
 * 过滤前: [图片1] [主体1] [null] [图片 2] [    ]
 * 过滤后: [图片1] [主体1] [图片 2]
 *
 * 第五步：添加剩余图片 (当前长度: 3, 剩余槽位: 4, 无剩余图片)
 * 添加后: [图片1] [主体1] [图片2]
 *
 * 第六步：重新命名
 * 重命名后: [图片 1] [主体1] [图片 2]
 *
 * 最终输出：
 * newReferencePrompts: [图片 1] [主体1] [图片 2]
 *
 *
 * 注意事项：
 * - 最多支持 7 个引用提示槽位
 * - 只处理状态为 'done' 且包含有效图片内容的提示
 * - 图片名称会根据当前语言环境进行本地化处理
 *
 * @param imagePrompts 当前表单中的图片提示数组，可能包含 null 值
 * @param referencePrompts 当前的引用提示数组，默认为空数组
 * @param locale 当前语言环境，用于生成本地化的图片名称，默认为 'en'
 * @returns 更新后的引用提示数组，包含同步后的图片数据
 */
const mergeImagePromptsIntoReferencePrompts = (
  imagePrompts: (FormImagePrompt | null)[],
  referencePrompts: ReferencePrompt[] = [],
  locale: SupportedLanguage = 'en',
): ReferencePrompt[] => {
  // 第一步：创建新的引用提示数组的副本，避免直接修改原数组
  const newReferencePrompts: ReferencePrompt[] = [];
  const newImagePrompts = [...imagePrompts];
  // 第二步：遍历现有的引用提示数组，替换已有的图片类型的项目
  referencePrompts.forEach((ref, index) => {
    // 如果当前引用提示是图片类型
    if (ref.type === 'image' && newImagePrompts.length > 0) {
      // 检查是否有对应索引的有效图片提示可以填充
      const pushItem = newImagePrompts.shift();
      // 从有效图片提示数组中取出第一个元素
      if (pushItem) {
        // 更新当前引用提示的内容 - 直接修改数组中的元素
        newReferencePrompts.push({
          type: 'image',
          id: pushItem.id,
          name: `图片 ${index + 1}`, // 临时名称，后续会被重新命名
          status: pushItem.status,
          prompt: pushItem.prompt,
        });
      }
    } else if (ref) {
      newReferencePrompts.push(ref);
    }
  });

  // 第四步：过滤出有效的图片提示
  const validImagePrompts = newImagePrompts.filter(
    (img): img is FormImagePrompt => img?.status === 'done' && img.prompt !== null,
  );
  // 第五步：如果引用提示数量少于7个，将剩余的有效图片提示添加进去
  // 这确保了所有有效的图片都能被包含在引用提示中，但总数不超过7个
  const remainingSlots = 7 - newReferencePrompts.length;
  if (remainingSlots > 0 && validImagePrompts.length > 0) {
    // 只取能添加的数量，确保总长度不超过7
    const imagesToAdd = validImagePrompts.slice(0, remainingSlots);
    newReferencePrompts.push(
      ...imagesToAdd.map(
        // 将剩余的有效图片提示转换为引用提示格式
        (img): ReferencePrompt => ({
          type: 'image',
          id: img.id,
          name: `图片 ${img.id}`, // 临时名称，后续会被重新命名
          status: img.status,
          prompt: img.prompt,
        }),
      ),
    );
  }

  // 第六步：重新生成图片名称，用于回填表单和切换到 Q1 模型时的显示
  // 为保证主题库文本和图片@对应的逻辑
  let imgIndex = 1;
  // biome-ignore lint/suspicious/useIterableCallbackReturn: this map is only used to mutate prompt names in place
  newReferencePrompts.map((ref) => {
    if (ref.type === 'image') {
      ref.name = getNextImageName(imgIndex, locale);
      imgIndex++;
    }
  });

  // 返回处理后的引用提示数组
  return newReferencePrompts;
};
